import { useState, useTransition } from "react";
import { useNavigate } from "react-router";
import { useLocale, useTranslations } from "use-intl";
import { CalendarPlus, Dumbbell, Footprints, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toIsoDate } from "@liftquest/shared/dates";
import { lt } from "@liftquest/shared/localized";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { startSession } from "@/lib/actions";
import { ApiError } from "@/lib/api";
import { useDashboard } from "@/lib/queries";
import { cn } from "@/lib/utils";

/** Same limit as the API. */
const MAX_DAYS_BACK = 366;

/** Picks a workout of the active plan and a past date, then opens the trainer to enter the sets. */
export function LogPastWorkoutButton() {
  const t = useTranslations("progress");
  const tc = useTranslations("common");
  const locale = useLocale();
  const navigate = useNavigate();
  const plan = useDashboard().data?.plan;
  const [open, setOpen] = useState(false);
  const [workoutId, setWorkoutId] = useState<string | null>(null);
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return toIsoDate(d);
  });
  const [time, setTime] = useState("18:00");
  const [pending, start] = useTransition();
  if (!plan || plan.workouts.length === 0) return null;

  const now = new Date();
  const earliest = new Date(now);
  earliest.setDate(earliest.getDate() - MAX_DAYS_BACK + 1);
  // "2026-10-30T18:00" without an offset is local time.
  const startedAt = new Date(`${date}T${time || "00:00"}`);
  const valid = !Number.isNaN(startedAt.getTime()) && startedAt <= now && startedAt >= earliest;
  const selected = plan.workouts.find((w) => w.id === workoutId && w.exercises.length > 0) ?? null;

  const submit = () =>
    start(async () => {
      if (!selected || !valid) return;
      try {
        const { id } = await startSession({ workoutId: selected.id, startedAt: startedAt.toISOString() });
        setOpen(false);
        navigate(`/train/${id}`);
      } catch (e) {
        toast.error(e instanceof ApiError && e.status === 409 ? t("openSessionFirst") : tc("error"));
      }
    });

  return (
    <>
      <Button variant="outline" size="sm" className="rounded-full" onClick={() => setOpen(true)}>
        <CalendarPlus /> {t("logPast")}
      </Button>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{t("logPastTitle")}</DrawerTitle>
            <DrawerDescription>{t("logPastText")}</DrawerDescription>
          </DrawerHeader>
          <form
            className="space-y-4 px-4 pb-8"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <fieldset className="space-y-2">
              <legend className="mb-2 text-sm font-medium">{t("workout")}</legend>
              {plan.workouts.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  disabled={w.exercises.length === 0}
                  aria-pressed={workoutId === w.id}
                  onClick={() => setWorkoutId(w.id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition disabled:opacity-50",
                    workoutId === w.id ? "border-primary bg-primary/10" : "hover:bg-muted/50",
                  )}
                >
                  {w.kind === "cardio" ? (
                    <Footprints className="size-5 shrink-0 text-chart-3" />
                  ) : (
                    <Dumbbell className="size-5 shrink-0 text-primary" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{lt(w.name, locale)}</span>
                    <span className="block truncate text-xs text-muted-foreground">{lt(w.focus, locale)}</span>
                  </span>
                </button>
              ))}
            </fieldset>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="past-date">{t("date")}</Label>
                <Input
                  id="past-date"
                  type="date"
                  required
                  value={date}
                  min={toIsoDate(earliest)}
                  max={toIsoDate(now)}
                  onChange={(e) => setDate(e.target.value)}
                  className="h-11 text-base"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="past-time">{t("startTime")}</Label>
                <Input
                  id="past-time"
                  type="time"
                  required
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="h-11 text-base"
                />
              </div>
            </div>
            {!valid && <p className="text-sm text-destructive">{t("pastDateHint")}</p>}
            <Button type="submit" size="lg" className="h-12 w-full rounded-2xl text-base" disabled={!selected || !valid || pending}>
              {pending && <Loader2 className="animate-spin" />}
              {t("enterSets")}
            </Button>
          </form>
        </DrawerContent>
      </Drawer>
    </>
  );
}
